import type { CSSProperties } from 'react';
import type { TransformedCSSProperties } from './component.types';

/**
 * Intercept flex properties in style, and certain `props` props (like `gap`, for fun like `gap: small` using design system).
 *
 * @todo proper, plez
 */

export const transformSpecialCSSProperties = <
  T extends TransformedCSSProperties & { style?: CSSProperties },
>(
  props: T
) => {
  /** Pick out certain styles that we want to themify */

  const {
    style: restStyles,
    alignContent,
    alignItems,
    alignSelf,
    flex,
    flexBasis,
    flexDirection,
    flexFlow,
    flexGrow,
    flexShrink,
    flexWrap,
    gap,
    gridTemplateColumns,
    gridColumn,
    height,
    justifyContent,
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    maxHeight,
    maxWidth,
    minHeight,
    minWidth,
    overflow,
    padding,
    width,
    ...restProps
  } = props;

  /**
   * Consider using classes for some of these CSS properties
   * instead of styles, but this is a good start.
   */

  const convertBoolFlexValue = (
    value: React.CSSProperties['flexGrow'] | boolean
  ): React.CSSProperties['flexGrow'] => (typeof value === 'boolean' ? (value ? 1 : 0) : value);

  const style: React.CSSProperties = {
    ...restStyles,
    gap: (sizeToVariableMap as Record<any, string>)[gap!] || gap || restStyles?.gap,
    padding: (sizeToVariableMap as Record<any, string>)[padding!] || padding || restStyles?.padding,
    margin: (sizeToVariableMap as Record<any, string>)[margin!] || margin || restStyles?.margin,
    marginTop:
      (sizeToVariableMap as Record<any, string>)[marginTop!] || marginTop || restStyles?.marginTop,
    marginBottom:
      (sizeToVariableMap as Record<any, string>)[marginBottom!] ||
      marginBottom ||
      restStyles?.marginBottom,

    marginLeft:
      (sizeToVariableMap as Record<any, string>)[marginLeft!] ||
      marginLeft ||
      restStyles?.marginLeft,
    marginRight:
      (sizeToVariableMap as Record<any, string>)[marginRight!] ||
      marginRight ||
      restStyles?.marginRight,
    flex: flex ?? restStyles?.flex,
    flexBasis: flexBasis ?? restStyles?.flexBasis,
    flexDirection: flexDirection ?? restStyles?.flexDirection,
    flexFlow: flexFlow ?? restStyles?.flexFlow,
    flexGrow: convertBoolFlexValue(flexGrow) ?? restStyles?.flexGrow,
    flexShrink: convertBoolFlexValue(flexShrink) ?? restStyles?.flexShrink,
    flexWrap: flexWrap ?? restStyles?.flexWrap,
    alignItems: alignItems ?? restStyles?.alignItems,
    alignContent: alignContent ?? restStyles?.alignContent,
    alignSelf: alignSelf ?? restStyles?.alignSelf,
    justifyContent: justifyContent ?? restStyles?.justifyContent,
    width: width ?? restStyles?.width,
    height: height ?? restStyles?.height,
    maxWidth: maxWidth ?? restStyles?.maxWidth,
    maxHeight: maxHeight ?? restStyles?.maxHeight,
    minWidth: minWidth ?? restStyles?.minWidth,
    minHeight: minHeight ?? restStyles?.minHeight,
    overflow: overflow ?? restStyles?.overflow,
    gridTemplateColumns: gridTemplateColumns ?? restStyles?.gridTemplateColumns,
    gridColumn: gridColumn ?? restStyles?.gridColumn,
  };

  // Too many empty keys seemed to cause issues, maybe
  (Object.keys(style) as Array<keyof CSSProperties>).forEach(key => {
    if (typeof style[key] === 'undefined') {
      delete style[key];
    }
  });

  return {
    style,
    ...restProps,
  };
};

const sizeToVariableMap = {
  small: 'var(--reference_size_01)',
  medium: 'var(--reference_size_03)',
  large: 'var(--reference_size_04)',
  xlarge: 'var(--reference_size_05)',
};
